import classcat from "classcat";
import React, { SyntheticEvent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  IconBasket,
  IconExclamationCircle,
  IconTickCircle,
} from "@touchtech/web-baselayer-ui";

import { TranslationContext } from "@is/constants";
import { useOutsideSyntheticClick, useWidgetProductDetails } from "@is/hooks";
import { MediaOrderingRule, ProductItem, WidgetType } from "@is/types";
import { getMainImageUrl } from "@is/utils";

import css from "../WidgetProductItem.module.css";

type Props = {
  item: ProductItem;
  onClick: (item: ProductItem) => void;
  onQuickAddToBasket: (item: ProductItem) => void;
  quickAddToBasketItem: ProductItem | null;
  onQuickAddToBasketRemoveItem: () => void;
  quickAddToBasketMode: boolean;
  widgetType: WidgetType;
};

const useManageWidgetProductItem = ({
  item,
  onClick,
  onQuickAddToBasket,
  quickAddToBasketItem,
  onQuickAddToBasketRemoveItem,
  quickAddToBasketMode,
  widgetType,
}: Props) => {
  const { t } = useTranslation(TranslationContext.ProductDetails);

  const {
    selectedColorVariantId,
    selectedSize,
    selectedLength,
    selectedColorVariant,
    variants,
    sizes,
    lengths,
    colorName,
    variantsWithSelectedSize,
    sizesForColorVariant,
    lengthsForColorVariant,
    isBasketItemsPending,
    isAdded,
    isLengthRequired,
    showOutOfStockNotification,
    showUnavailableSlash,
    setSelectedColorVariantId,
    setSelectedSize,
    setSelectedLength,
    handleSelectVariant,
    handleSelectSize,
    handleSelectLength,
    handleBasketButtonClick,
  } = useWidgetProductDetails({
    item,
    skipRequest: !quickAddToBasketItem,
  });

  const ref = useOutsideSyntheticClick((event) => {
    if (quickAddToBasketMode && quickAddToBasketItem) {
      event.stopPropagation();
      event.preventDefault();

      setSelectedColorVariantId(item.id);
      setSelectedSize(null);
      setSelectedLength(null);
      onQuickAddToBasketRemoveItem();
    }
  });

  const basketButtonContent = useMemo(() => {
    let contentText = t("add to basket");

    let contentIcon = (
      <IconBasket className={classcat([css.basketIcon, "text-label-base"])} />
    );

    if (showOutOfStockNotification) {
      contentIcon = (
        <IconExclamationCircle
          className={classcat([css.basketIcon, "text-label-base"])}
        />
      );
      contentText = t("out of stock");
    } else if (isAdded) {
      contentIcon = (
        <IconTickCircle
          className={classcat([css.basketIcon, "text-label-base"])}
        />
      );
      contentText = t("added!");
    } else if (!selectedSize) {
      contentIcon = null;
      contentText = t("select size");
    } else if (isLengthRequired && !selectedLength) {
      contentIcon = null;
      contentText = t("select length");
    }

    return (
      <>
        {contentIcon} {contentText}
      </>
    );
  }, [
    t,
    showOutOfStockNotification,
    isAdded,
    selectedSize,
    isLengthRequired,
    selectedLength,
  ]);

  const mediaUrl = useMemo(() => {
    const medias = selectedColorVariant?.media
      ? selectedColorVariant.media
      : item.media;

    const orderRule =
      widgetType === WidgetType.AllColors
        ? MediaOrderingRule.MediaSlider
        : MediaOrderingRule.Widget;

    return getMainImageUrl(medias, orderRule);
  }, [item, selectedColorVariant, widgetType]);

  const handleClick = useCallback(() => {
    onClick(item);
  }, [item, onClick]);

  const handleQuickAddToBasket = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();

      onQuickAddToBasket(item);
    },
    [item, onQuickAddToBasket]
  );

  return {
    selectedColorVariantId,
    selectedSize,
    selectedLength,
    sizes,
    lengths,
    variants,
    mediaUrl,
    handleSelectVariant,
    handleSelectSize,
    handleSelectLength,
    handleClick,
    handleQuickAddToBasket,
    ref,
    basketButtonContent,
    handleBasketButtonClick,
    isAdded,
    showOutOfStockNotification,
    isLengthRequired,
    variantsWithSelectedSize,
    sizesForColorVariant,
    lengthsForColorVariant,
    showUnavailableSlash,
    colorName,
    isBasketItemsPending,
    t,
  };
};

export default useManageWidgetProductItem;
